<template>
  <div
    class="c-app d-flex align-items-center min-vh-100"
    :class="{ 'c-dark-theme': $store.state.darkMode }"
  >
    <CContainer fluid>
      <ToastContainer :toasts="toasts" />

      <CRow class="justify-content-center">
        <CCol md="9" lg="7" xl="6">
          <CCard class="mx-4 mb-0">
            <CCardBody class="p-5">
              <div class="text-center mb-4">
                <img
                  :src="logoUrl"
                  alt="Logo"
                  class="img-fluid mb-3"
                  style="max-height: 80px"
                />
                <h1 class="h4 font-weight-bold mb-2 text-marketing-primary">
                  Start your free 14-day trial
                </h1>
                <p class="text-muted mb-2">No credit card required • Cancel anytime</p>
                <p class="text-muted small">
                  <CIcon name="cil-check-circle" class="mr-1" />
                  Join 500+ Malaysian businesses using Niagaku
                </p>
              </div>

              <CForm>
                <!-- Business Name Field -->
                <div class="mb-3">
                  <label class="font-weight-semibold mb-1 small">
                    Business name *
                  </label>
                  <CInput
                    placeholder="Your Business Name"
                    autocomplete="organization"
                    v-model="loginObj.businessName"
                    :is-valid="(!isValidBusinessName && loginObj.businessName.length > 0) ? false : null"
                    @input="validateBusinessName"
                    aria-label="Business Name"
                  >
                    <template #prepend-content>
                      <CIcon name="cil-building" />
                    </template>
                  </CInput>
                  <small
                    v-if="!isValidBusinessName && loginObj.businessName.length > 0"
                    class="text-danger d-block"
                  >
                    Enter your business name
                  </small>
                </div>

                <!-- Full Name Field -->
                <div class="mb-3">
                  <label class="font-weight-semibold mb-1 small">
                    Full name *
                  </label>
                  <CInput
                    placeholder="Your Full Name"
                    autocomplete="name"
                    v-model="loginObj.fullName"
                    :is-valid="(!isValidFullName && loginObj.fullName.length > 0) ? false : null"
                    @input="validateFullName"
                    aria-label="Full Name"
                  >
                    <template #prepend-content>
                      <CIcon name="cil-user" />
                    </template>
                  </CInput>
                  <small
                    v-if="!isValidFullName && loginObj.fullName.length > 0"
                    class="text-danger d-block"
                  >
                    Enter your full name
                  </small>
                </div>

                <!-- Email Field -->
                <div class="mb-3">
                  <label class="font-weight-semibold mb-1 small">
                    Email *
                  </label>
                  <CInput
                    placeholder="name@company.com"
                    autocomplete="email"
                    prepend="@"
                    v-model="loginObj.email"
                    :is-valid="(!isValidEmail && loginObj.email.length > 0) ? false : null"
                    @input="validateEmail"
                    aria-label="Email Address"
                  />
                  <small
                    v-if="!isValidEmail && loginObj.email.length > 0"
                    class="text-danger d-block"
                  >
                    Enter an email like name@company.com
                  </small>
                </div>

                <!-- Password Field with Show/Hide Toggle -->
                <div class="mb-3">
                  <label class="font-weight-semibold mb-1 small">
                    Password *
                  </label>
                  <div class="position-relative">
                    <CInput
                      placeholder="Minimum 8 characters"
                      :type="showPassword ? 'text' : 'password'"
                      autocomplete="new-password"
                      v-model="loginObj.password"
                      :is-valid="(!isValidPassword && loginObj.password.length > 0) ? false : null"
                      @input="validatePassword"
                      aria-label="Password"
                    >
                      <template #prepend-content>
                        <CIcon name="cil-lock-locked" />
                      </template>
                    </CInput>
                    <button
                      type="button"
                      @click="showPassword = !showPassword"
                      class="btn btn-link position-absolute"
                      style="right: 10px; top: 50%; transform: translateY(-50%); padding: 0; z-index: 5;"
                      :aria-label="showPassword ? 'Hide password' : 'Show password'"
                    >
                      <CIcon :name="showPassword ? 'cil-eye-slash' : 'cil-eye'" class="text-muted" />
                    </button>
                  </div>
                  <small
                    v-if="!isValidPassword && loginObj.password.length > 0"
                    class="text-danger d-block"
                  >
                    Use at least 8 characters
                  </small>

                  <!-- Password Strength Indicator -->
                  <div v-if="loginObj.password.length > 0" class="mt-2">
                    <div class="d-flex align-items-center mb-1">
                      <small class="text-muted mr-2">Password strength:</small>
                      <small :class="'text-' + passwordStrengthColor" class="font-weight-semibold">
                        {{ passwordStrengthText }}
                      </small>
                    </div>
                    <div class="progress progress-xs">
                      <div
                        class="progress-bar"
                        :class="'bg-' + passwordStrengthColor"
                        :style="{ width: passwordStrengthPercent + '%' }"
                      ></div>
                    </div>
                  </div>
                </div>

                <!-- Confirm Password Field with Show/Hide Toggle -->
                <div class="mb-3">
                  <label class="font-weight-semibold mb-1 small">
                    Confirm password *
                  </label>
                  <div class="position-relative">
                    <CInput
                      placeholder="Re-enter your password"
                      :type="showPasswordConfirm ? 'text' : 'password'"
                      autocomplete="new-password"
                      v-model="loginObj.passwordConfirmation"
                      :is-valid="(!passwordsMatch && loginObj.passwordConfirmation.length > 0) ? false : null"
                      @input="validatePasswordConfirmation"
                      aria-label="Confirm Password"
                    >
                      <template #prepend-content>
                        <CIcon name="cil-lock-locked" />
                      </template>
                    </CInput>
                    <button
                      type="button"
                      @click="showPasswordConfirm = !showPasswordConfirm"
                      class="btn btn-link position-absolute"
                      style="right: 10px; top: 50%; transform: translateY(-50%); padding: 0; z-index: 5;"
                      :aria-label="showPasswordConfirm ? 'Hide password' : 'Show password'"
                    >
                      <CIcon :name="showPasswordConfirm ? 'cil-eye-slash' : 'cil-eye'" class="text-muted" />
                    </button>
                  </div>
                  <small
                    v-if="!passwordsMatch && loginObj.passwordConfirmation.length > 0"
                    class="text-danger d-block"
                  >
                    The two passwords don't match. Type them again
                  </small>
                </div>

                <!-- Terms of Service Checkbox -->
                <div class="mb-4">
                  <div class="custom-control custom-checkbox">
                    <input
                      type="checkbox"
                      class="custom-control-input"
                      id="termsCheckbox"
                      v-model="agreedToTerms"
                    />
                    <label class="custom-control-label small" for="termsCheckbox">
                      I agree to the Terms of Service and Privacy Policy
                    </label>
                  </div>
                  <small v-if="showTermsError" class="text-danger d-block">
                    Tick the box to agree to the Terms of Service and Privacy Policy
                  </small>
                </div>

                <!-- Submit Button -->
                <CButton
                  :disabled="loading || !canSubmit"
                  block
                  @click.prevent="register"
                  class="btn-marketing-accent font-weight-bold"
                >
                  <CSpinner v-if="loading" size="sm" />
                  {{ loading ? 'Creating account…' : 'Create free account' }}
                </CButton>
              </CForm>

              <!-- Link to Login Page -->
              <p class="text-center mt-4 mb-0">
                <span class="text-muted">Already have an account?</span>
                <router-link
                  to="/login"
                  class="ml-1 text-marketing-accent font-weight-bold"
                >
                  Log in
                </router-link>
              </p>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>
    </CContainer>
  </div>
</template>

<script>
import ToastContainer from '@/views/widgets/ToastContainer.vue'

export default {
  name: 'Register',
  components: {
    ToastContainer
  },
  data: () => {
    return {
      toasts: [],
      loginObj: {
        businessName: '',
        fullName: '',
        email: '',
        password: '',
        passwordConfirmation: '',
      },
      loading: false,
      isValidBusinessName: true,
      isValidFullName: true,
      isValidEmail: true,
      isValidPassword: true,
      passwordsMatch: true,
      showPassword: false,
      showPasswordConfirm: false,
      agreedToTerms: false,
      showTermsError: false,
    };
  },
  computed: {
    logoUrl() {
      return this.$store.state.logoUrl || '/img/logo.png'
    },
    canSubmit() {
      return (
        this.isValidBusinessName &&
        this.isValidFullName &&
        this.isValidEmail &&
        this.isValidPassword &&
        this.passwordsMatch &&
        this.agreedToTerms &&
        this.loginObj.businessName.length > 0 &&
        this.loginObj.fullName.length > 0 &&
        this.loginObj.email.length > 0 &&
        this.loginObj.password.length >= 8 &&
        this.loginObj.passwordConfirmation.length >= 8
      )
    },
    passwordStrength() {
      const password = this.loginObj.password
      if (password.length === 0) return 0

      let strength = 0
      if (password.length >= 8) strength += 25
      if (password.length >= 12) strength += 25
      if (/[a-z]/.test(password)) strength += 15
      if (/[A-Z]/.test(password)) strength += 15
      if (/[0-9]/.test(password)) strength += 10
      if (/[^a-zA-Z0-9]/.test(password)) strength += 10

      return Math.min(strength, 100)
    },
    passwordStrengthText() {
      if (this.passwordStrength === 0) return ''
      if (this.passwordStrength < 40) return 'Weak'
      if (this.passwordStrength < 70) return 'Medium'
      return 'Strong'
    },
    passwordStrengthColor() {
      if (this.passwordStrength < 40) return 'danger'
      if (this.passwordStrength < 70) return 'secondary'
      return 'success'
    },
    passwordStrengthPercent() {
      return this.passwordStrength
    }
  },
  methods: {
    navigateToLogin() {
      this.$router.push({ path: '/login' });
    },
    register() {
      this.validateBusinessName()
      this.validateFullName()
      this.validateEmail()
      this.validatePassword()
      this.validatePasswordConfirmation()

      if (!this.agreedToTerms) {
        this.showTermsError = true
        return
      }

      if (!this.canSubmit) {
        return
      }

      this.loading = true
      var registerDto = {
        email: this.loginObj.email,
        password: this.loginObj.password,
        passwordConfirmation: this.loginObj.passwordConfirmation,
        businessName: this.loginObj.businessName,
        fullName: this.loginObj.fullName,
      }

      auth
        .register(registerDto)
        .then(() => {
          this.loading = false
          this.$router.push({ path: '/pages/registerCompleted' })
        })
        .catch((error) => {
          this.loading = false
          console.error(error)
          const data = error && (error.data || (error.response && error.response.data))
          this.addToast('Error', "Couldn't create your account. " + helper.getErrorMessage(data), 'danger')
        })
    },
    addToast(header, message, color) {
      this.toasts.push({
        header: header,
        message: message,
        color: color,
        show: true
      })
    },
    validateBusinessName() {
      this.isValidBusinessName = this.loginObj.businessName.trim().length > 0
    },
    validateFullName() {
      this.isValidFullName = this.loginObj.fullName.trim().length > 0
    },
    validateEmail() {
      const regex = /^\S+@\S+\.\S+$/
      this.isValidEmail = regex.test(this.loginObj.email)
    },
    validatePassword() {
      this.isValidPassword = this.loginObj.password.length >= 8
    },
    validatePasswordConfirmation() {
      this.passwordsMatch = this.loginObj.password === this.loginObj.passwordConfirmation
    },
  },
  watch: {
    agreedToTerms(newVal) {
      if (newVal) {
        this.showTermsError = false
      }
    }
  }
}
</script>

<style scoped>
.progress-xs {
  height: 4px;
}
</style>